import { NetworkError } from "../../api/errors";
import type { Me } from "../../api/schemas";
import { isValidTimeZone } from "../../domain/time";
import type { RoleSnapshot } from "../../offline/model";
import { ownerKey } from "./ownerKey";

/*
 * Last known roles for the navigation after an app start without connection (F-05, ADR-0104).
 *
 * The roles only decide which navigation entries are visible. They are no authorization decision:
 * the server checks every request on its own, and screens that need the server keep handling a
 * missing connection as before.
 */

/**
 * Maximum age of a role snapshot. An older snapshot is ignored, so after a long time without a
 * successful `GET /me` the navigation shows only the entries without role requirement (ADR-0104).
 */
export const roleSnapshotMaxAgeMs = 30 * 24 * 60 * 60 * 1000;

/**
 * Creates the snapshot of a successful `GET /me`. The answer is only kept if it belongs to the
 * tenant the request was meant for: with a chosen tenant (T-105) the server must have answered for
 * exactly that tenant, otherwise the selection changed during the request.
 *
 * @param me - The response.
 * @param subject - OIDC subject of the signed-in account.
 * @param selectedTenantId - The chosen tenant, or `null` if none was chosen.
 * @param fetchedAtMs - Device time when the request started, in milliseconds.
 * @returns The snapshot, or `null` if the answer does not belong to the chosen tenant.
 */
export function createRoleSnapshot(
  me: Me,
  subject: string,
  selectedTenantId: string | null,
  fetchedAtMs: number,
): RoleSnapshot | null {
  if (selectedTenantId !== null && selectedTenantId !== me.tenantId) {
    return null;
  }

  const snapshot: RoleSnapshot = {
    ownerSubject: ownerKey(subject, selectedTenantId),
    subject,
    tenantId: me.tenantId,
    roles: [...me.roles],
    timeZoneId: me.employment?.timeZoneId ?? null,
    hasEmployment: me.employment !== null,
    fetchedAtMs,
  };

  return snapshot;
}

/**
 * Returns a stored snapshot only if it belongs to the signed-in person and tenant and is not older
 * than {@link roleSnapshotMaxAgeMs}.
 *
 * @param snapshot - The stored snapshot, or `null`.
 * @param owner - Owner key of the signed-in person and chosen tenant.
 * @param nowMs - Current device time in milliseconds.
 * @returns The usable snapshot, or `null`.
 */
export function usableRoleSnapshot(
  snapshot: RoleSnapshot | null,
  owner: string,
  nowMs: number,
): RoleSnapshot | null {
  if (snapshot?.ownerSubject !== owner) {
    return null;
  }

  const ageMs = nowMs - snapshot.fetchedAtMs;
  const usable = ageMs <= roleSnapshotMaxAgeMs ? snapshot : null;

  return usable;
}

/**
 * Returns the time zone of the employment stored with the last profile of the signed-in person
 * (F-18, ADR-0132): only from a usable snapshot ({@link usableRoleSnapshot}) and only a zone this
 * device knows, so a damaged value never breaks the clock or the synchronization.
 *
 * @param snapshot - The stored snapshot, or `null`.
 * @param owner - Owner key of the signed-in person and chosen tenant.
 * @param nowMs - Current device time in milliseconds.
 * @returns The IANA time zone, or `null` if none is usable.
 */
export function storedWorkTimeZone(
  snapshot: RoleSnapshot | null,
  owner: string,
  nowMs: number,
): string | null {
  const zone = usableRoleSnapshot(snapshot, owner, nowMs)?.timeZoneId ?? null;
  const known = zone !== null && isValidTimeZone(zone) ? zone : null;

  return known;
}

/** Sources of the roles shown in the navigation. */
export interface NavigationRoleSources {
  /** Roles from `GET /me` of this session, or `undefined` while they load or offline. */
  readonly fresh: readonly string[] | undefined;
  /** Error of the last `GET /me`, or `null`. */
  readonly error: Error | null;
  /** The usable snapshot of the signed-in person ({@link usableRoleSnapshot}), or `null`. */
  readonly snapshot: RoleSnapshot | null;
}

/**
 * Chooses the roles for the navigation: the roles from the server whenever they are known, else the
 * last known roles while the server has not answered (loading, offline, unreachable). If the server
 * answered with an error (for example a membership that ended), it stays authoritative and the
 * snapshot is not used.
 *
 * @param sources - The sources.
 * @returns The roles, or `undefined` if they are not known.
 */
export function selectNavigationRoles(
  sources: NavigationRoleSources,
): readonly string[] | undefined {
  if (sources.fresh !== undefined) {
    return sources.fresh;
  }

  const unanswered = sources.error === null || sources.error instanceof NetworkError;
  const roles = unanswered && sources.snapshot !== null ? sources.snapshot.roles : undefined;

  return roles;
}

/** Sources of {@link selectHasEmployment}. */
export interface EmploymentSources {
  /** Profile from `GET /me` of this session, or `undefined` while it loads, is paused or failed. */
  readonly profile: Me | undefined;
  /** Error of the last `GET /me`, or `null`. */
  readonly error: Error | null;
  /** The usable snapshot of the signed-in person ({@link usableRoleSnapshot}), or `null`. */
  readonly snapshot: RoleSnapshot | null;
}

/**
 * Chooses whether the signed-in person has an employment, which decides whether the compact clock
 * of the app bar is offered (F-75): from the profile whenever it is known, else from the last
 * profile stored on this device while the server has not answered, like the roles of the
 * navigation ({@link selectNavigationRoles}).
 *
 * @param sources - The sources.
 * @returns Whether an employment exists, or `undefined` if that is not known (no answer and no
 * snapshot, or a snapshot of an earlier app version).
 */
export function selectHasEmployment(sources: EmploymentSources): boolean | undefined {
  if (sources.profile !== undefined) {
    const fromProfile = sources.profile.employment !== null;
    return fromProfile;
  }

  const unanswered = sources.error === null || sources.error instanceof NetworkError;
  const stored = unanswered ? sources.snapshot?.hasEmployment : undefined;

  return stored;
}
